import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getItem } from "../../../../../Store/Store/Item/Item";
import { getProject } from "../../../../../Store/Store/Project/Project";
import { getLocation } from "../../../../../Store/Store/StoreMaster/InventoryLocation/Location";
import { getViewStockAdjustmentItems } from "../../../../../Store/prosafe/proTransaction/StockAdjustment/getViewStockAdjustmentItems";
import toast from "react-hot-toast";
import { V_URL } from "../../../../../BaseUrl";
import axios from "axios";
import Footer from "../../../Include/Footer";
import Breadcrumb from "../../../../../Components/Breadcrumb/Breadcrumb";
import Header from "../../../Include/Header";
import Sidebar from "../../../Include/Sidebar";
import Loader from "../../../Include/Loader";
import moment from "moment";
import { Dropdown } from "primereact/dropdown";

const REASON_OPTIONS = [
    "Issued without slip (Shop Floor / Production)",
    "Physical Verification Variance / Cycle Count",
    "Damaged / Broken / Handling Loss",
    "Cutting / Scrap / Calibration Variance",
    "Unrecorded Supplier Excess Delivery",
    "Return from Floor without Documentation",
    "Initial Balance / System Setup",
    "Other (Specify in Remarks)",
];

const formatUnit = (u) => {
    if (!u) return "";
    if (typeof u === "object") return u.name || "";
    return String(u);
};

const ManageStockAdjustment = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state: editData } = useLocation();

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [disable, setDisable] = useState(false);

    // Header Form
    const [form, setForm] = useState({
        trans_date: moment(Date.now()).format("YYYY-MM-DD"),
        project_id: "",
        store_location_id: "",
        manual_pro_no: "",
        adjustment_type: "MINUS",
        adjustment_reason: "Issued without slip (Shop Floor / Production)",
        remarks: "",
        auto_approve: false,
    });

    // Item Entry State
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [selectedItemObj, setSelectedItemObj] = useState(null);
    const [liveStock, setLiveStock] = useState({
        current_stock: 0,
        loading: false,
    });
    const [inputMode, setInputMode] = useState("physical_count"); // "physical_count" or "direct_qty"
    const [physicalQty, setPhysicalQty] = useState("");
    const [adjustmentQty, setAdjustmentQty] = useState("");
    const [itemRate, setItemRate] = useState(0);
    const [itemRemarks, setItemRemarks] = useState("");

    // Array of adjusted items
    const [itemsArr, setItemsArr] = useState([]);

    useEffect(() => {
        dispatch(getItem({ is_main: true }));
        dispatch(getProject());
        dispatch(getLocation());
    }, [dispatch]);

    // Handle Edit Data loading
    useEffect(() => {
        if (editData?._id) {
            dispatch(getViewStockAdjustmentItems({ formData: { id: editData._id } }));
        }
    }, [editData, dispatch]);

    const editDoc = useSelector(
        (state) => state.getViewStockAdjustmentItems?.user?.data
    );
    const isEditLoading = useSelector(
        (state) => state.getViewStockAdjustmentItems?.loading
    );

    useEffect(() => {
        if (editData?._id && editDoc) {
            setForm({
                trans_date: moment(editDoc.trans_date).format("YYYY-MM-DD"),
                project_id:
                    editDoc.project_id?._id ||
                    editDoc.project_id ||
                    editDoc.project?._id ||
                    "",
                store_location_id:
                    editDoc.store_location_id?._id || editDoc.store_location_id || "",
                manual_pro_no: editDoc.manual_pro_no || "",
                adjustment_type: editDoc.adjustment_type || "MINUS",
                adjustment_reason:
                    editDoc.adjustment_reason ||
                    "Issued without slip (Shop Floor / Production)",
                remarks: editDoc.remarks || "",
                auto_approve: editDoc.status === 3,
            });

            if (editDoc.items_details?.length > 0) {
                const formattedItems = editDoc.items_details.map((itm) => ({
                    item_id: itm.item_id?._id || itm.item_id,
                    item_code: itm.item_id?.code || itm.item_code || "",
                    item_name: itm.item_id?.name || itm.item_name || "",
                    unit: formatUnit(itm.item_id?.unit || itm.unit),
                    system_qty: itm.system_qty || 0,
                    physical_qty: itm.physical_qty || 0,
                    quantity: itm.quantity || 0,
                    variance_qty: itm.variance_qty || itm.quantity || 0,
                    rate: itm.rate || 0,
                    total_amount: itm.total_amount || 0,
                    adjustment_reason: itm.adjustment_reason || "",
                    remarks: itm.remarks || "",
                    project_id: itm.project_id || null,
                }));
                setItemsArr(formattedItems);
            }
        }
    }, [editData, editDoc]);

    const itemMasterList =
        useSelector((state) => state.getItem?.user?.data) || [];
    const projectList =
        useSelector((state) => state.getProject?.user?.data) || [];
    const locationList =
        useSelector((state) => state.getLocation?.user?.data) || [];

    // Searchable dropdown options for Material / Items
    const itemDropdownOptions = useMemo(() => {
        return (itemMasterList || []).map((itm) => ({
            label: `${itm.code ? `[${itm.code}] ` : ""}${itm.name}${itm.material_grade ? ` (${itm.material_grade})` : ""
                }`,
            value: itm._id,
            item: itm,
            filterLabel: `${itm.code || ""} ${itm.name || ""} ${itm.material_grade || ""
                } ${itm.detail || ""}`.toLowerCase(),
        }));
    }, [itemMasterList]);

    // Searchable dropdown options for Projects
    const projectDropdownOptions = useMemo(() => {
        return (projectList || []).map((p) => ({
            label: `${p.name} ${p.project_no ? `(${p.project_no})` : ""}`,
            value: p._id,
        }));
    }, [projectList]);

    // Searchable dropdown options for Store Locations
    const locationDropdownOptions = useMemo(() => {
        return (locationList || []).map((loc) => ({
            label: loc.name,
            value: loc._id,
        }));
    }, [locationList]);

    // Searchable dropdown options for Reasons
    const reasonDropdownOptions = useMemo(() => {
        return REASON_OPTIONS.map((r) => ({
            label: r,
            value: r,
        }));
    }, []);

    // Fetch live stock when item or project changes
    useEffect(() => {
        if (!selectedItemId) {
            setSelectedItemObj(null);
            setLiveStock({ current_stock: 0, loading: false });
            setItemRate(0);
            return;
        }

        const found = itemMasterList.find((i) => i._id === selectedItemId);
        setSelectedItemObj(found || null);
        if (found) {
            setItemRate(found.purchase_rate || found.rate || found.sale_rate || 0);
        }

        fetchItemLiveStock(selectedItemId, form.project_id);
    }, [selectedItemId, form.project_id, itemMasterList]);

    const fetchItemLiveStock = async (itemId, projectId) => {
        if (!itemId) return;
        setLiveStock({ current_stock: 0, loading: true });
        try {
            const response = await axios.post(
                `${V_URL}/prosafe/get-live-item-stock`,
                { item_id: itemId, project_id: projectId || undefined },
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
                    },
                }
            );

            if (response.data.success) {
                const stock = response.data.data?.current_stock || 0;
                setLiveStock({ current_stock: stock, loading: false });

                if (physicalQty !== "") {
                    calculateFromPhysical(Number(physicalQty), stock);
                }
            } else {
                setLiveStock({ current_stock: 0, loading: false });
            }
        } catch (error) {
            console.error("Live stock error:", error);
            setLiveStock({ current_stock: 0, loading: false });
        }
    };

    const calculateFromPhysical = (phy, sysStock) => {
        const variance = phy - sysStock;
        if (variance < 0) {
            setAdjustmentQty(Math.abs(variance).toString());
            if (form.adjustment_type !== "MINUS") {
                setForm((prev) => ({ ...prev, adjustment_type: "MINUS" }));
            }
        } else if (variance > 0) {
            setAdjustmentQty(variance.toString());
            if (form.adjustment_type !== "PLUS") {
                setForm((prev) => ({ ...prev, adjustment_type: "PLUS" }));
            }
        } else {
            setAdjustmentQty("0");
        }
    };

    const handlePhysicalQtyChange = (val) => {
        setPhysicalQty(val);
        if (val === "") {
            setAdjustmentQty("");
            return;
        }
        const num = parseFloat(val);
        if (!isNaN(num)) {
            calculateFromPhysical(num, liveStock.current_stock);
        }
    };

    const handleAdjustmentQtyChange = (val) => {
        setAdjustmentQty(val);
        if (val === "") {
            setPhysicalQty("");
            return;
        }
        const num = parseFloat(val);
        if (!isNaN(num)) {
            if (form.adjustment_type === "MINUS") {
                setPhysicalQty((liveStock.current_stock - num).toString());
            } else {
                setPhysicalQty((liveStock.current_stock + num).toString());
            }
        }
    };

    const handleAddItem = () => {
        if (!selectedItemId || !selectedItemObj) {
            toast.error("Please select a material item");
            return;
        }

        const qty = parseFloat(adjustmentQty);
        if (isNaN(qty) || qty <= 0) {
            toast.error("Adjustment quantity must be greater than 0");
            return;
        }

        const exists = itemsArr.find((i) => i.item_id === selectedItemId);
        if (exists) {
            toast.error(
                "This item is already added to the voucher. Please remove it first to re-add."
            );
            return;
        }

        const sysQty = liveStock.current_stock;
        const phyQty =
            physicalQty !== ""
                ? parseFloat(physicalQty)
                : form.adjustment_type === "MINUS"
                    ? sysQty - qty
                    : sysQty + qty;
        const rate = parseFloat(itemRate) || 0;
        const amount = qty * rate;

        const newItem = {
            item_id: selectedItemId,
            item_code: selectedItemObj.code || "",
            item_name: selectedItemObj.name || "",
            unit: formatUnit(selectedItemObj.unit),
            system_qty: sysQty,
            physical_qty: phyQty,
            quantity: qty,
            variance_qty: form.adjustment_type === "PLUS" ? qty : -qty,
            rate: rate,
            total_amount: amount,
            adjustment_reason: form.adjustment_reason,
            remarks: itemRemarks,
            project_id: form.project_id || null,
        };

        setItemsArr([...itemsArr, newItem]);

        // Reset inputs
        setSelectedItemId(null);
        setSelectedItemObj(null);
        setPhysicalQty("");
        setAdjustmentQty("");
        setItemRemarks("");
        toast.success("Item added to adjustment voucher");
    };

    const handleRemoveItem = (index) => {
        const updated = [...itemsArr];
        updated.splice(index, 1);
        setItemsArr(updated);
    };

    const totalValuation = itemsArr.reduce(
        (sum, item) => sum + (item.total_amount || 0),
        0
    );
    const totalQty = itemsArr.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0
    );

    const handleSubmit = async () => {
        if (!itemsArr.length) {
            toast.error("Please add at least one material item to adjust");
            return;
        }

        setDisable(true);
        try {
            const payload = {
                firm_id: localStorage.getItem("PAY_USER_FIRM_ID"),
                year_id: localStorage.getItem("PAY_USER_YEAR_ID"),
                trans_date: form.trans_date,
                project_id: form.project_id || null,
                store_location_id: form.store_location_id || null,
                adjustment_type: form.adjustment_type,
                adjustment_reason: form.adjustment_reason,
                remarks: form.remarks,
                manual_pro_no: form.manual_pro_no,
                items_details: JSON.stringify(itemsArr),
                status: form.auto_approve ? 3 : 1,
                created_by: localStorage.getItem("PAY_USER_ID"),
                modified_by: localStorage.getItem("PAY_USER_ID"),
            };

            if (editData?._id) {
                payload.id = editData._id;
            }

            const response = await axios.post(
                `${V_URL}/prosafe/pro-add-stock-adjustment`,
                payload,
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
                    },
                }
            );

            if (response.data.success) {
                toast.success(
                    form.auto_approve
                        ? "Stock adjustment approved & stock updated immediately!"
                        : "Stock adjustment created & submitted for approval!"
                );
                navigate("/main-store/user/stock-adjustment-management");
            } else {
                toast.error(
                    response.data.message || "Failed to save stock adjustment"
                );
            }
        } catch (err) {
            console.error(err);
            toast.error(
                err.response?.data?.message ||
                "Something went wrong saving stock adjustment"
            );
        } finally {
            setDisable(false);
        }
    };

    const handleReset = () => {
        setForm({
            trans_date: moment(Date.now()).format("YYYY-MM-DD"),
            project_id: "",
            store_location_id: "",
            manual_pro_no: "",
            adjustment_type: "MINUS",
            adjustment_reason: "Issued without slip (Shop Floor / Production)",
            remarks: "",
            auto_approve: false,
        });
        setSelectedItemId(null);
        setSelectedItemObj(null);
        setPhysicalQty("");
        setAdjustmentQty("");
        setItemRemarks("");
        setItemsArr([]);
    };

    // Custom Item Option Template for PrimeReact Dropdown
    const itemOptionTemplate = (option) => {
        if (!option || !option.item) return option?.label || "";
        const itm = option.item;
        return (
            <div className="d-flex justify-content-between align-items-center py-1">
                <div>
                    <span className="fw-semibold text-dark">{itm.name}</span>
                    {itm.code && (
                        <span className="badge badge-soft-secondary ms-2 fw-normal">
                            {itm.code}
                        </span>
                    )}
                    {itm.material_grade && (
                        <small className="text-muted d-block">{itm.material_grade}</small>
                    )}
                </div>
                <span className="badge badge-soft-primary ms-2">
                    {formatUnit(itm.unit) || "UOM"}
                </span>
            </div>
        );
    };

    const breadcrumb = [
        { label: "Dashboard", link: "/main-store/user/dashboard" },
        {
            label: "Stock Adjustment",
            link: "/main-store/user/stock-adjustment-management",
        },
        {
            label: editData?._id
                ? "Edit Stock Adjustment"
                : "Add Stock Adjustment",
            active: true,
        },
    ];

    const isMinus = form.adjustment_type === "MINUS";

    return (
        <div className={`main-wrapper ${isSidebarOpen ? "slide-nav" : ""}`}>
            <Header handleOpen={() => setIsSidebarOpen(!isSidebarOpen)} />
            <Sidebar />
            <div className="page-wrapper">
                <div className="content">
                    <Breadcrumb items={breadcrumb} />

                    {isEditLoading ? (
                        <Loader />
                    ) : (
                        <div>
                            {/* 1. Header Information & Adjustment Direction Card */}
                            <div className="card">
                                <div className="card-body">
                                    <div className="form-heading d-flex flex-wrap justify-content-between align-items-center mb-3">
                                        <h4 className="mb-2 mb-md-0">
                                            {editData?._id ? "Edit" : "Add"} Stock Adjustment Details
                                        </h4>

                                        {/* Attractive Pill Segmented Control for Inward / Outward */}
                                        <div className="d-inline-flex p-1 rounded-pill bg-light border">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setForm({ ...form, adjustment_type: "MINUS" })
                                                }
                                                className="btn btn-sm rounded-pill px-3 py-1"
                                                style={{
                                                    backgroundColor: isMinus ? "#ef4444" : "transparent",
                                                    color: isMinus ? "#ffffff" : "#64748b",
                                                    fontWeight: isMinus ? "600" : "500",
                                                    border: "none",
                                                    boxShadow: isMinus
                                                        ? "0 2px 4px rgba(239, 68, 68, 0.3)"
                                                        : "none",
                                                    fontSize: "13px",
                                                    transition: "all 0.2s ease",
                                                }}
                                            >
                                                <i className="fa-solid fa-arrow-trend-down me-1"></i>
                                                Stock Outward (-) / Loss
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setForm({ ...form, adjustment_type: "PLUS" })
                                                }
                                                className="btn btn-sm rounded-pill px-3 py-1"
                                                style={{
                                                    backgroundColor: !isMinus ? "#10b981" : "transparent",
                                                    color: !isMinus ? "#ffffff" : "#64748b",
                                                    fontWeight: !isMinus ? "600" : "500",
                                                    border: "none",
                                                    boxShadow: !isMinus
                                                        ? "0 2px 4px rgba(16, 185, 129, 0.3)"
                                                        : "none",
                                                    fontSize: "13px",
                                                    transition: "all 0.2s ease",
                                                }}
                                            >
                                                <i className="fa-solid fa-arrow-trend-up me-1"></i>
                                                Stock Inward (+) / Surplus
                                            </button>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-12 col-md-6 col-xl-3">
                                            <div className="input-block local-forms">
                                                <label>
                                                    Adjustment Date <span className="login-danger">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={form.trans_date}
                                                    onChange={(e) =>
                                                        setForm({ ...form, trans_date: e.target.value })
                                                    }
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6 col-xl-3">
                                            <div className="input-block local-forms custom-select-wpr">
                                                <label>
                                                    Primary Reason Code{" "}
                                                    <span className="login-danger">*</span>
                                                </label>
                                                <Dropdown
                                                    value={form.adjustment_reason}
                                                    options={reasonDropdownOptions}
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            adjustment_reason: e.value,
                                                        })
                                                    }
                                                    placeholder="Select Reason"
                                                    filter
                                                    filterBy="label"
                                                    className="w-100"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6 col-xl-3">
                                            <div className="input-block local-forms custom-select-wpr">
                                                <label>Project</label>
                                                <Dropdown
                                                    value={form.project_id || null}
                                                    options={projectDropdownOptions}
                                                    onChange={(e) =>
                                                        setForm({ ...form, project_id: e.value })
                                                    }
                                                    placeholder="Select Project"
                                                    filter
                                                    filterBy="label"
                                                    showClear={!!form.project_id}
                                                    className="w-100"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6 col-xl-3">
                                            <div className="input-block local-forms custom-select-wpr">
                                                <label>Store Location</label>
                                                <Dropdown
                                                    value={form.store_location_id || null}
                                                    options={locationDropdownOptions}
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            store_location_id: e.value,
                                                        })
                                                    }
                                                    placeholder="Select Store Location"
                                                    filter
                                                    filterBy="label"
                                                    showClear={!!form.store_location_id}
                                                    className="w-100"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6 col-xl-3">
                                            <div className="input-block local-forms">
                                                <label>Manual Slip / Ref No.</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="e.g. AUDIT-2026/01"
                                                    value={form.manual_pro_no}
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            manual_pro_no: e.target.value,
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6 col-xl-9">
                                            <div className="input-block local-forms">
                                                <label>Remarks / Justification</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter audit remarks or root cause..."
                                                    value={form.remarks}
                                                    onChange={(e) =>
                                                        setForm({ ...form, remarks: e.target.value })
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 2. Material Selector & Live Stock Reconciler Card */}
                            <div className="card">
                                <div className="card-body">
                                    <div className="form-heading d-flex flex-wrap justify-content-between align-items-center mb-3">
                                        <h4 className="mb-2 mb-md-0">
                                            Add Materials to Adjust
                                        </h4>

                                        {/* Attractive Mode Switcher */}
                                        <div className="d-inline-flex p-1 rounded-pill bg-light border">
                                            <button
                                                type="button"
                                                onClick={() => setInputMode("physical_count")}
                                                className="btn btn-sm rounded-pill px-3 py-1"
                                                style={{
                                                    backgroundColor:
                                                        inputMode === "physical_count"
                                                            ? "#2e384d"
                                                            : "transparent",
                                                    color:
                                                        inputMode === "physical_count"
                                                            ? "#ffffff"
                                                            : "#64748b",
                                                    fontWeight:
                                                        inputMode === "physical_count" ? "600" : "500",
                                                    border: "none",
                                                    boxShadow:
                                                        inputMode === "physical_count"
                                                            ? "0 2px 4px rgba(46, 56, 77, 0.25)"
                                                            : "none",
                                                    fontSize: "13px",
                                                    transition: "all 0.2s ease",
                                                }}
                                            >
                                                <i className="fa-solid fa-list-check me-1"></i> Shelf Count Mode
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setInputMode("direct_qty")}
                                                className="btn btn-sm rounded-pill px-3 py-1"
                                                style={{
                                                    backgroundColor:
                                                        inputMode === "direct_qty"
                                                            ? "#2e384d"
                                                            : "transparent",
                                                    color:
                                                        inputMode === "direct_qty"
                                                            ? "#ffffff"
                                                            : "#64748b",
                                                    fontWeight:
                                                        inputMode === "direct_qty" ? "600" : "500",
                                                    border: "none",
                                                    boxShadow:
                                                        inputMode === "direct_qty"
                                                            ? "0 2px 4px rgba(46, 56, 77, 0.25)"
                                                            : "none",
                                                    fontSize: "13px",
                                                    transition: "all 0.2s ease",
                                                }}
                                            >
                                                <i className="fa-solid fa-calculator me-1"></i> Direct Delta Mode
                                            </button>
                                        </div>
                                    </div>

                                    <div className="row">
                                        {/* Material Item Dropdown */}
                                        <div className="col-12 col-md-6 col-lg-4">
                                            <div className="input-block local-forms custom-select-wpr">
                                                <label>
                                                    Select Material / Item <span className="login-danger">*</span>
                                                </label>
                                                <Dropdown
                                                    value={selectedItemId}
                                                    options={itemDropdownOptions}
                                                    onChange={(e) => setSelectedItemId(e.value)}
                                                    itemTemplate={itemOptionTemplate}
                                                    placeholder="Select Item"
                                                    filter
                                                    filterBy="filterLabel"
                                                    showClear={!!selectedItemId}
                                                    className="w-100"
                                                />
                                            </div>
                                        </div>

                                        {/* Current System Stock */}
                                        <div className="col-12 col-md-6 col-lg-2">
                                            <div className="input-block local-forms">
                                                <label className="text-muted">Current System Stock</label>
                                                <div
                                                    className="form-control d-flex align-items-center justify-content-between bg-light"
                                                    style={{ height: "44px" }}
                                                >
                                                    {liveStock.loading ? (
                                                        <span className="text-muted small">
                                                            <i className="fa-solid fa-spinner fa-spin me-1"></i> Loading...
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <span className="text-dark">
                                                                {liveStock.current_stock.toFixed(2)}
                                                            </span>
                                                            <span className="badge badge-soft-info ms-1">
                                                                {formatUnit(selectedItemObj?.unit) || "UOM"}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Mode 1: Physical Count Mode */}
                                        {inputMode === "physical_count" ? (
                                            <>
                                                <div className="col-12 col-md-6 col-lg-2">
                                                    <div className="input-block local-forms">
                                                        <label>Shelf Physical Count</label>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            className="form-control"
                                                            placeholder="Enter Counted Qty"
                                                            value={physicalQty}
                                                            onChange={(e) =>
                                                                handlePhysicalQtyChange(e.target.value)
                                                            }
                                                            disabled={!selectedItemId}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-12 col-md-6 col-lg-2">
                                                    <div className="input-block local-forms">
                                                        <label className="text-muted">Calculated Variance</label>
                                                        <div
                                                            className={`form-control text-center ${isMinus
                                                                    ? "text-danger bg-soft-danger"
                                                                    : "text-success bg-soft-success"
                                                                }`}
                                                            style={{ height: "44px", lineHeight: "30px" }}
                                                        >
                                                            {adjustmentQty
                                                                ? `${isMinus ? "-" : "+"}${adjustmentQty} ${formatUnit(
                                                                    selectedItemObj?.unit
                                                                )}`
                                                                : "0.00"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            /* Mode 2: Direct Adjustment Delta Mode */
                                            <>
                                                <div className="col-12 col-md-6 col-lg-2">
                                                    <div className="input-block local-forms">
                                                        <label>
                                                            Adjust Qty ({isMinus ? "- Out" : "+ In"})
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            className="form-control"
                                                            placeholder="Enter Qty"
                                                            value={adjustmentQty}
                                                            onChange={(e) =>
                                                                handleAdjustmentQtyChange(e.target.value)
                                                            }
                                                            disabled={!selectedItemId}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-12 col-md-6 col-lg-2">
                                                    <div className="input-block local-forms">
                                                        <label className="text-muted">Resulting Stock</label>
                                                        <div
                                                            className="form-control text-dark text-center bg-light"
                                                            style={{ height: "44px", lineHeight: "30px" }}
                                                        >
                                                            {physicalQty
                                                                ? `${parseFloat(physicalQty).toFixed(2)} ${formatUnit(
                                                                    selectedItemObj?.unit
                                                                )}`
                                                                : "-"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {/* Unit Rate */}
                                        <div className="col-12 col-md-6 col-lg-2">
                                            <div className="input-block local-forms">
                                                <label>Unit Rate (₹)</label>
                                                <input
                                                    type="number"
                                                    step="any"
                                                    className="form-control"
                                                    value={itemRate}
                                                    onChange={(e) => setItemRate(e.target.value)}
                                                    disabled={!selectedItemId}
                                                />
                                            </div>
                                        </div>

                                        {/* Line Specific Remarks */}
                                        <div className="col-12 col-md-12 col-lg-12">
                                            <div className="input-block local-forms">
                                                <label>Item Specific Remarks / Batch Notes</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="e.g. Issued to fabrication welder or heat no."
                                                    value={itemRemarks}
                                                    onChange={(e) => setItemRemarks(e.target.value)}
                                                    disabled={!selectedItemId}
                                                />
                                            </div>
                                        </div>

                                        {/* Add Item Button at bottom right */}
                                        <div className="col-12 text-end">
                                            <button
                                                type="button"
                                                className="btn btn-primary submit-form"
                                                style={{ minWidth: "120px", height: "40px", padding: "0 20px" }}
                                                onClick={handleAddItem}
                                                disabled={
                                                    !selectedItemId ||
                                                    !adjustmentQty ||
                                                    parseFloat(adjustmentQty) <= 0
                                                }
                                            >
                                                <i className="fa-solid fa-plus me-1"></i> Add Item
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 3. Adjusted Items Breakdown Table */}
                            <div className="card">
                                <div className="card-body">
                                    <div className="form-heading d-flex justify-content-between align-items-center mb-3">
                                        <h4 className="mb-0">
                                            Adjusted Items Breakdown ({itemsArr.length})
                                        </h4>
                                        <span
                                            className={`custom-badge ${isMinus ? "status-pink" : "status-green"
                                                }`}
                                        >
                                            {isMinus
                                                ? "Outward (-) Deducting Stock"
                                                : "Inward (+) Adding Stock"}
                                        </span>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table border-0 custom-table comman-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: "5%" }} className="text-center">
                                                        Sr.
                                                    </th>
                                                    <th>Material Item</th>
                                                    <th style={{ width: "8%" }} className="text-center">
                                                        UOM
                                                    </th>
                                                    <th style={{ width: "12%" }} className="text-end">
                                                        System Stock
                                                    </th>
                                                    <th style={{ width: "12%" }} className="text-end">
                                                        Shelf Count
                                                    </th>
                                                    <th style={{ width: "12%" }} className="text-end">
                                                        Adjusted Delta
                                                    </th>
                                                    <th style={{ width: "10%" }} className="text-end">
                                                        Rate (₹)
                                                    </th>
                                                    <th style={{ width: "12%" }} className="text-end">
                                                        Valuation (₹)
                                                    </th>
                                                    <th style={{ width: "18%" }}>Remarks / Reason</th>
                                                    <th style={{ width: "7%" }} className="text-end">
                                                        Action
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {itemsArr.length > 0 ? (
                                                    itemsArr.map((row, idx) => (
                                                        <tr key={idx}>
                                                            <td className="text-center">{idx + 1}</td>
                                                            <td>
                                                                <span className="fw-semibold text-dark">
                                                                    {row.item_name || "N/A"}
                                                                </span>
                                                                {row.item_code && (
                                                                    <span className="badge badge-soft-secondary fw-normal ms-2">
                                                                        {row.item_code}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="text-center">
                                                                <span className="badge badge-soft-info">
                                                                    {formatUnit(row.unit) || "-"}
                                                                </span>
                                                            </td>
                                                            <td className="text-end text-muted">
                                                                {parseFloat(row.system_qty || 0).toFixed(2)}
                                                            </td>
                                                            <td className="text-end fw-semibold">
                                                                {parseFloat(row.physical_qty || 0).toFixed(2)}
                                                            </td>
                                                            <td
                                                                className="text-end fw-bold"
                                                                style={{
                                                                    color: isMinus ? "#e63946" : "#2a9d8f",
                                                                }}
                                                            >
                                                                {isMinus ? "-" : "+"}
                                                                {parseFloat(row.quantity || 0).toFixed(2)}
                                                            </td>
                                                            <td className="text-end">
                                                                ₹{parseFloat(row.rate || 0).toFixed(2)}
                                                            </td>
                                                            <td className="text-end fw-semibold">
                                                                ₹{parseFloat(row.total_amount || 0).toFixed(2)}
                                                            </td>
                                                            <td>
                                                                <small
                                                                    className="d-block text-truncate"
                                                                    style={{ maxWidth: "220px" }}
                                                                >
                                                                    {row.remarks || row.adjustment_reason || "-"}
                                                                </small>
                                                            </td>
                                                            <td className="text-end">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger p-1"
                                                                    onClick={() => handleRemoveItem(idx)}
                                                                    title="Remove item"
                                                                >
                                                                    <i className="fa-solid fa-trash-can"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="10">
                                                            <div className="no-table-data">
                                                                No items added yet. Select a material above and click "Add Item".
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                            {itemsArr.length > 0 && (
                                                <tfoot className="table-light fw-bold">
                                                    <tr>
                                                        <td colSpan="5" className="text-end">
                                                            Total Summary:
                                                        </td>
                                                        <td
                                                            className="text-end fw-bold"
                                                            style={{
                                                                color: isMinus ? "#e63946" : "#2a9d8f",
                                                            }}
                                                        >
                                                            {isMinus ? "-" : "+"}
                                                            {totalQty.toFixed(2)}
                                                        </td>
                                                        <td></td>
                                                        <td className="text-end text-primary fs-6">
                                                            ₹{totalValuation.toFixed(2)}
                                                        </td>
                                                        <td colSpan="2"></td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* 4. Action & Submit Buttons strictly matching ManagePurReq */}
                            <div className="row">
                                <div className="col-sm-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="row align-items-center">
                                                <div className="col-12 col-md-7 mb-3 mb-md-0">
                                                    <div className="form-check form-switch mb-0 d-inline-flex align-items-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="autoApproveSwitch"
                                                            checked={form.auto_approve}
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    auto_approve: e.target.checked,
                                                                })
                                                            }
                                                            style={{ cursor: "pointer", width: "2.5em", height: "1.3em" }}
                                                        />
                                                        <label
                                                            className="form-check-label ms-2 fw-semibold text-dark mb-0"
                                                            htmlFor="autoApproveSwitch"
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            Directly Approve & Update Stock Ledger Immediately
                                                            <small className="d-block text-muted fw-normal">
                                                                {form.auto_approve
                                                                    ? "Stock ledger will be updated immediately upon saving."
                                                                    : "Voucher will be saved as Pending for verification & approval."}
                                                            </small>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="col-12 col-md-5 text-end">
                                                    <div className="doctor-submit text-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary submit-form me-2"
                                                            onClick={handleSubmit}
                                                            disabled={disable || !itemsArr.length}
                                                        >
                                                            {disable
                                                                ? "Processing..."
                                                                : editData?._id
                                                                    ? "Update"
                                                                    : form.auto_approve
                                                                        ? "Save & Approve"
                                                                        : "Submit"}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary cancel-form"
                                                            onClick={handleReset}
                                                        >
                                                            Reset
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <Footer />
            </div>
        </div>
    );
};

export default ManageStockAdjustment;
